<script>
	import API from '../States/API';
	import Data from '../States/Data';
	import Config from '../Models/Config';
	import Helper from '../Models/Helper';
	import Session from '../States/Session';
	import Slider from '../States/Slider';
	import Suggest from './Search/Suggest.svelte';
	import Trigger from '../Models/Trigger';
	import {onDestroy} from 'svelte';

	export let index = 0;

	let properties = {};
	let birds = {};
	let active = false;

	async function preloadBirds() {
		if ($Data.version.birds) {
			await Helper.script('./scripts/three.js');
			await Helper.script('./scripts/birds.js');
		}
	}

	function startBirds() {
		if ($Data.version.birds) {
			const BB = $Data.version.code === 'BB';
			const options = {
				el: '#app', minHeight: 200, minWidth: 200, scale: 1.00, scaleMobile: 1,
				backgroundAlpha: 0, colorMode: 'lerp',
				backgroundColor: $Session.theme.dark ? 0x08131E : 0xF3F9FC,
				color1: $Session.theme.dark ? (BB ? 0x193D5F : 0x0C2612) : 0x143d52,
				color2: $Session.theme.dark ? (BB ? 0x0D2033 : 0x1B6C65) : 0xC3BE12,
				quantity: Helper.mediumScreen() ? 1 : 1,
				speedLimit: Helper.smallScreen() ? 1 : 10
			};

			if (!Helper.is(birds))
				birds = VANTA.BIRDS(options);
		}
	}

	function stopBirds() {
		if (Helper.is(birds)) {
			birds.destroy();
			birds = {};
		}
	}

	Slider.subscribe(slides => {
		active = Slider.check(index, 'start', properties);
		if (active) {
			properties = Slider.properties(index);
			active = properties.active;
			if (!active) stopBirds();
		}
	});

	Trigger.subscribe('theme', state => {
		if (active) {
			stopBirds();
			startBirds();
		}
	});

	async function init() {
		await preloadBirds();
		startBirds();
	}

	onDestroy(() => {
		stopBirds();
	});

	init();
</script>

<style>
	div.box div.content {
		background: transparent;
	}

	div.box div.content h1 {
		font-weight: 400;
		margin-top: .75em;
		margin-bottom: 0;
		font-size: 2em;
	}

	div.box div.content h2 {
		font-size: 1.5em;
		font-weight: 300;
		line-height: 1.2em;
		margin-top: .3em;
		margin-bottom: 0;
	}

	div.box div.content h3 {
		font-size: 1em;
		font-weight: 300;
		margin-top: .1em;
		margin-bottom: 1.5em;
		opacity: .75;
	}

	div.navigation {
		margin-top: 1em;
	}

	div.navigation .button {
		text-align: left;
		color: var(--primary-color);
	}

	div.navigation .button i {
		font-size: 1.25em;
		text-align: center;
		color: var(--tertiary-color);
		vertical-align: middle;
		margin-right: .5em;
		width: 1.25em;
	}

	div.social {
		text-align: left;
		padding-top: 2em;
	}

	div.social > a {
		display: inline-block;
		margin-right: 1.2em;
	}

	div.social > a i {
		font-size: 1.5em;
		color: var(--tertiary-color);
		opacity: .75;
	}

	/* Banner */

	div.banner div.banner-label {
		font-size: .75em;
		color: var(--primary-color);
		margin-left: .5em;
		margin-top: -1.5em;
		opacity: .5;
	}

	div.banner a.banner-container {
		display: flex;
		flex-direction: row;
		gap: 1em;
		background: var(--box-background-light);
		border-radius: .5em;
		padding: .4em 1em .6em 1em;
		border: 1px solid var(--primary-background-light);
	}

	div.banner a.banner-container img {
		width: 25%;
		max-width: 7em;
		margin-right: .5em;
	}

	div.banner a.banner-container div.banner-content {
		flex-grow: 1;
		color: var(--primary-color);
	}

	div.banner a.banner-container div.banner-content div.banner-header {
		font-weight: 600;
	}

	div.banner a.banner-container div.banner-content div.banner-header span {
		line-height: 1em;
		color: var(--tertiary-color);
	}

	div.banner a.banner-container div.banner-content div.banner-hint {
		font-size: .9em;
		line-height: 1em;
	}
</style>

<div class="box start" class:active>
	<div class="content">
		{#if $Data.version.code === 'BB'}
			<div class="banner">
				<div class="banner-label">Advertentie</div>
				<a class="banner-container" href="{Helper.app() ? 'market://details?id=com.vensterbijbel.app' : 'https://www.vensterbijbel.nl'}" target="_blank">
					<img class="light" src="/media/logo/VB/light.svg" alt="VensterBijbel"/>
					<img class="dark" src="/media/logo/VB/dark.svg" alt="VensterBijbel"/>
					<div class="banner-content">
						<div class="banner-header"><span>Nieuw:</span> de VensterBijbel</div>
						<div class="banner-hint">Gebaseerd op de Statenvertaling</div>
					</div>
				</a>
			</div>
		{/if}
		{#if $Session.authentication.active}
			<h1>Welkom {$Session.user.name},</h1>
			<h2>bij de {$Data.version.name}.</h2>
		{:else}
			<h1>{$Data.version.name}</h1>
			{#if !$Data.version.beta}
				<h2>{$Data.version.description}</h2>
			{:else}
				<h2>Deze website en tekst zijn nog in ontwikkeling.</h2>
			{/if}
		{/if}
		<h3>{$Data.version.note}</h3>
		<Suggest host="start" query=""/>
		<div class="navigation">
			{#if $Session.bookmark.chapter}
				<div class="button attention" on:click="{event => Slider.route(['chapter', [$Session.bookmark.chapter]])}">
					<i class="fad fa-bookmark"></i> Verder Lezen - {$Session.bookmark.name}
				</div>
			{:else}
				<div class="button" on:click="{event => Slider.route('read')}">
					<i class="fad fa-book-open"></i> Beginnen met Lezen
				</div>
			{/if}
			<a class="button" href="/oude-testament">
				<i class="fad fa-books"></i> Bijbelboek Kiezen
			</a>
			{#if $Session.authentication.active}
				<div class="button" on:click="{event => Slider.route('favorite')}">
					<i class="fad fa-book-heart"></i>
					<span>Favoriete Verzen</span>
				</div>
			{/if}
			{#if $API.active}
				{#if $Session.authentication.active}
					<div class="button" on:click="{event => Slider.signOut()}">
						<i class="fad fa-sign-out"></i>
						<span>Uitloggen</span>
					</div>
				{:else}
					<div class="button" on:click="{event => Slider.route('signIn')}">
						<i class="fad fa-sign-in"></i>
						<span>Inloggen</span>
					</div>
				{/if}
			{/if}
			{#if !$Data.version.beta}
				<div class="button" on:click="{event => Slider.route('help')}">
					<i class="fad fa-question-circle"></i> Help
				</div>
			{/if}
			<div class="button" on:click="{event => Slider.route('info')}">
				<i class="fad fa-info-circle"></i> Info &amp; Contact
			</div>
			{#if $API.active && $Data.version.paypal && !Helper.app()}
				<div class="button" on:click="{event => window.open(Config.paypal)}">
					<i class="far fa-euro-sign"></i> Giften via PayPal
				</div>
			{/if}
		</div>
		{#if $API.active && $Data.version.social}
			<div class="social">
				<a href="https://facebook.com/BasisBijbel" target="_blank">
					<i class="fab fa-facebook"></i>
				</a>
				<a href="https://instagram.com/basisbijbel" target="_blank">
					<i class="fab fa-instagram"></i>
				</a>
			</div>
		{/if}
	</div>
</div>